"use client";

import { useState, useMemo, useEffect } from "react";
import { useApp } from "@/context/AppContext";
import CommonHeader from "./common/CommonHeader";
import { ActivitySearchBar } from "./activities/ActivitySearchBar";
import { ActivityFilter } from "./activities/ActivityFilter";
import { ActivitiesTable } from "./activities/ActivitiesTable";
import { Activity } from "@/types";
import { activityService } from "@/services/activityService";

export function ActivityManagement() {
  const [activities, setActivities] = useState<Activity[]>([]);
  // const { activities } = useApp();

  const [searchTerm, setSearchTerm] = useState("");
  const [filterAction, setFilterAction] = useState<string>("all");
  const [filterModule, setFilterModule] = useState<string>("all");
  const [sortConfig, setSortConfig] = useState<{
    key: "createdAt" | "action" | "module" | "description";
    direction: "asc" | "desc";
  }>({
    key: "createdAt",
    direction: "desc",
  });

  const fetchActivities = async () => {
    const activities = await activityService.getAllActivities();
    setActivities(activities);
  };

  useEffect(() => {
    fetchActivities();
  }, []);

  // console.log("activities", activities);

  const filteredAndSortedActivities = useMemo(() => {
    let filtered = activities.filter((activity) => {
      // Filter by search term
      const matchesSearch =
        searchTerm === "" ||
        activity.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        activity.module.toLowerCase().includes(searchTerm.toLowerCase());

      // Filter by action
      const matchesAction = filterAction === "all" || activity.action === filterAction;

      // Filter by module
      const matchesModule = filterModule === "all" || activity.module === filterModule;

      return matchesSearch && matchesAction && matchesModule;
    });

    // Sort activities
    filtered.sort((a, b) => {
      let aValue: string | Date;
      let bValue: string | Date;

      switch (sortConfig.key) {
        case "createdAt":
          aValue = new Date(a.createdAt);
          bValue = new Date(b.createdAt);
          break;
        case "action":
          aValue = a.action;
          bValue = b.action;
          break;
        case "module":
          aValue = a.module;
          bValue = b.module;
          break;
        case "description":
          aValue = a.description;
          bValue = b.description;
          break;
        default:
          return 0;
      }

      if (aValue instanceof Date && bValue instanceof Date) {
        return sortConfig.direction === "asc"
          ? aValue.getTime() - bValue.getTime()
          : bValue.getTime() - aValue.getTime();
      }

      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [activities, searchTerm, filterAction, filterModule, sortConfig]);

  const handleSort = (key: "createdAt" | "action" | "module" | "description") => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  return (
    <div className="space-y-6 w-full overflow-x-hidden">
      {/* Header */}
      <CommonHeader
        title="Activity Management"
        description="Track and monitor recent system activities"
        showButton={false}
      />

      {/* Search and Filter */}
      <div className="grid gap-4 md:grid-cols-3">
        <ActivitySearchBar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
        />
        <ActivityFilter
          filterAction={filterAction}
          filterModule={filterModule}
          onFilterActionChange={setFilterAction}
          onFilterModuleChange={setFilterModule}
        />
      </div>

      {/* Activities Table */}
      <ActivitiesTable
        activities={filteredAndSortedActivities}
        sortConfig={sortConfig}
        onSort={handleSort}
      />
    </div>
  );
}

